const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const ENABLED = document.documentElement.dataset.cScrollReveal !== 'false';

/** @type {IntersectionObserver | null} */
let observer = null;

function revealImmediately(elements) {
  elements.forEach((element) => {
    element.classList.add('c-reveal--visible');
  });
}

function revealInViewport(elements, { instant = false } = {}) {
  elements.forEach((element) => {
    if (element.classList.contains('c-reveal--visible')) return;

    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      if (instant) element.classList.add('c-reveal--instant');
      element.classList.add('c-reveal--visible');
      observer?.unobserve(element);
    }
  });
}

function observeElements(elements) {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('c-reveal--visible');
          observer?.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );
  }

  elements.forEach((element) => {
    if (element.classList.contains('c-reveal--visible')) return;
    observer?.observe(element);
  });
}

function collectRevealTargets(root = document) {
  const scope = root instanceof Element ? root : document;
  const targets = scope.querySelectorAll('.c-reveal:not(.c-reveal--visible)');

  if (root instanceof Element && root.matches('.c-reveal:not(.c-reveal--visible)')) {
    return [root, ...targets];
  }

  return [...targets];
}

function initScrollReveal(root = document) {
  const targets = collectRevealTargets(root);

  if (!targets.length) return;

  if (!ENABLED || REDUCED_MOTION || !('IntersectionObserver' in window)) {
    revealImmediately(targets);
    return;
  }

  observeElements(targets);
  // ponytail: above-fold nodes are already on screen — skip the fade so cold loads
  // don't flash visible → hidden (late CSS) → animate in again.
  revealInViewport(targets, { instant: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initScrollReveal(), { once: true });
} else {
  initScrollReveal();
}

document.addEventListener('shopify:section:load', (event) => {
  initScrollReveal(event.target);
});
document.addEventListener('shopify:section:reorder', () => initScrollReveal());

/*
 * Collection/search filtering re-renders the product grid via a morph wrapped in
 * a View Transition. Two things make cards stick at opacity:0 afterwards:
 *  1. Reused cards get their `c-reveal--visible` class wiped by the morph (server
 *     HTML only carries the base `c-reveal`), and they're no longer observed.
 *  2. During the View Transition snapshot the IntersectionObserver can miss cards
 *     that are actually on screen.
 */
function refreshReveal() {
  const targets = collectRevealTargets();
  if (!targets.length) return;

  if (!ENABLED || REDUCED_MOTION || !('IntersectionObserver' in window)) {
    revealImmediately(targets);
    return;
  }

  observeElements(targets);
  revealInViewport(targets, { instant: true });
}

document.addEventListener('filter:update', () => {
  requestAnimationFrame(() => requestAnimationFrame(refreshReveal));
  setTimeout(refreshReveal, 400);
  setTimeout(refreshReveal, 1000);
});

if ('MutationObserver' in window) {
  const pendingNodes = new Set();
  let flushFrame = 0;

  const flushPendingNodes = () => {
    flushFrame = 0;

    if (!pendingNodes.size) return;

    const targets = [...pendingNodes].flatMap((node) => collectRevealTargets(node));
    pendingNodes.clear();

    if (!targets.length) return;

    if (!ENABLED || REDUCED_MOTION || !('IntersectionObserver' in window)) {
      revealImmediately(targets);
      return;
    }

    observeElements(targets);
    revealInViewport(targets, { instant: true });
  };

  const queueRevealScan = (node) => {
    if (!(node instanceof Element)) return;
    pendingNodes.add(node);

    if (flushFrame) return;
    flushFrame = window.requestAnimationFrame(flushPendingNodes);
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        queueRevealScan(node);
      });
    });
  });

  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
}
